/**
 * @file The page an emailed password-reset link lands on (`/reset-password?token=...`).
 *
 * Reachable while SIGNED OUT (AppShell lets this one route through). PURE like AuthForm: it is
 * handed its actions and reports messages, so it renders and tests without a network.
 *
 * Three states, decided by the URL the API redirected to:
 *   - `?token=...`            the new-password form (password + confirm, same minimum as sign-up);
 *   - `?error=...` or no token the link is invalid, expired or already used: say so plainly and offer
 *                             a new one (the same request form as "Forgot password?");
 *   - success                 "Password updated", then on to sign-in. The server has signed every
 *                             session out, so sign-in is where the person lands.
 *
 * The token is only ever sent to the API. It is not stored, and not logged here.
 */
import { useState, type FormEvent } from "react";
import { MIN_PASSWORD_LENGTH, PASSWORD_RESET_LINK_MINUTES } from "@neighboros/shared";
import { RequestResetForm } from "./AuthScreen";
import { Button, Card, TextField } from "./ui";

export interface ResetPasswordScreenProps {
  /** The `token` query parameter, if present. */
  readonly token: string | undefined;
  /** The `error` query parameter the API adds to a bad link (e.g. INVALID_TOKEN). */
  readonly error: string | undefined;
  /** Resolves to a message (rejected token, rate limit) or null on success. */
  readonly onReset: (input: { token: string; newPassword: string }) => Promise<string | null>;
  /** Asks for a fresh link; same contract as the sign-in screen's "Forgot password?". */
  readonly onRequestPasswordReset: (email: string) => Promise<string | null>;
  /** Called after success, or from "Back to sign in". */
  readonly onDone: () => void;
}

function NewLinkCard({
  heading,
  explanation,
  onRequestPasswordReset,
  onDone,
}: {
  readonly heading: string;
  readonly explanation: string;
  readonly onRequestPasswordReset: (email: string) => Promise<string | null>;
  readonly onDone: () => void;
}) {
  return (
    <div className="space-y-4">
      <h1 className="text-center text-2xl font-semibold tracking-tight">{heading}</h1>
      <p role="alert" className="mx-auto max-w-sm text-center text-sm text-destructive">
        {explanation}
      </p>
      <RequestResetForm
        onRequest={onRequestPasswordReset}
        onBack={onDone}
        intro="Enter your email address and we will send you a new link."
      />
    </div>
  );
}

export function ResetPasswordScreen({
  token,
  error,
  onReset,
  onRequestPasswordReset,
  onDone,
}: ResetPasswordScreenProps) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  // A token the server refused: show the "request a new link" state rather than a dead form.
  const [rejected, setRejected] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <Card className="mx-auto w-full max-w-sm">
        <h1 className="text-lg font-semibold tracking-tight">Password updated</h1>
        <p role="status" className="mt-4 text-sm">
          Your password has been changed and you have been signed out everywhere. Sign in with the
          new password to continue.
        </p>
        <div className="mt-4">
          <Button onClick={onDone}>Go to sign in</Button>
        </div>
      </Card>
    );
  }

  if (error !== undefined || token === undefined || token.length === 0 || rejected) {
    return (
      <NewLinkCard
        heading="Reset your password"
        explanation={
          "This reset link is invalid or has expired. Links work once and last " +
          String(PASSWORD_RESET_LINK_MINUTES) + " minutes."
        }
        onRequestPasswordReset={onRequestPasswordReset}
        onDone={onDone}
      />
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submitting || token === undefined) return;
    if (password.length < MIN_PASSWORD_LENGTH) {
      setMessage(`Use at least ${String(MIN_PASSWORD_LENGTH)} characters for your password.`);
      return;
    }
    if (password !== confirm) {
      setMessage("The two passwords do not match.");
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      const failure = await onReset({ token, newPassword: password });
      if (failure === null) {
        setDone(true);
      } else if (/invalid|expired/i.test(failure)) {
        setRejected(true);
      } else {
        setMessage(failure);
      }
    } catch {
      setMessage("Could not reach the server. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-center text-2xl font-semibold tracking-tight">Choose a new password</h1>
      <Card className="mx-auto w-full max-w-sm">
        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)}>
          <TextField
            id="reset-password"
            label="New password"
            type="password"
            value={password}
            onChange={setPassword}
            required
            maxLength={128}
            autoComplete="new-password"
            hint={`At least ${String(MIN_PASSWORD_LENGTH)} characters.`}
          />
          <TextField
            id="reset-password-confirm"
            label="Confirm new password"
            type="password"
            value={confirm}
            onChange={setConfirm}
            required
            maxLength={128}
            autoComplete="new-password"
          />
          {message === null ? null : (
            <p role="alert" className="text-sm text-destructive">
              {message}
            </p>
          )}
          <Button type="submit" disabled={submitting}>
            {submitting ? "One moment…" : "Set new password"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
